import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import {
  CheckCircle2,
  ShieldCheck,
  ArrowRight,
  Phone,
  GraduationCap,
  Play,
  ChevronDown,
  X,
  Clock,
  FileText,
  Star,
  Users,
  Check,
  CalendarCheck,
  Apple,
  X as XIcon,
} from "lucide-react";
import { ASSETS } from "../mock";
import { track, getVisitorId, getSessionId, getStoredUTM, getCalendlyUtm } from "../analytics";
import {
  fireRegistrationConversion,
  fireGoogleAdsConversion,
  whopTrack,
  PIXEL_CONFIG,
} from "../pixels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const GREEN = "#16E36F";

const CTA =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-[#16E36F] px-8 py-4 text-base font-extrabold text-black shadow-[0_0_22px_rgba(22,227,111,0.35)] transition-all duration-200 hover:bg-[#13C760] hover:-translate-y-0.5";
const CARD =
  "rounded-xl border border-[#16E36F]/20 bg-[#0D0E10] p-6 transition-all duration-300 hover:border-[#16E36F]/50";

export const scrollToForm = () => {
  const el = document.getElementById("apply-form");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* ---------------- Urgency Bar ---------------- */
export const UrgencyBar = () => {
  const [open, setOpen] = useState(true);
  if (!open) return null;
  return (
    <div
      className="relative z-[60] flex items-center justify-center bg-[#16E36F] px-4 py-2 text-center"
      data-testid="urgency-bar"
    >
      <p className="text-sm font-bold text-black">
        Only 12 coaching spots remaining this cohort
      </p>
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label="Dismiss"
        data-testid="urgency-bar-close"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-black/70 transition-colors hover:text-black"
      >
        <X size={16} strokeWidth={3} />
      </button>
    </div>
  );
};

/* ---------------- Brand header ---------------- */
export const BrandHeader = () => (
  <div className="flex items-center justify-center gap-3 py-6" data-testid="brand-header">
    <img src={ASSETS.logoEmblem} alt="The Matrix Unlocked" className="h-10 w-10 object-contain" />
    <span className="text-sm font-bold uppercase tracking-[0.25em] text-white">
      The Matrix Unlocked
    </span>
  </div>
);

/* ---------------- Financial qualifier ---------------- */
export const FinancialQualifier = () => (
  <div className="mx-auto max-w-2xl px-4" data-testid="financial-qualifier">
    <div className="mb-4 flex justify-center">
      <span className="rounded-full border border-[#16E36F]/40 bg-[#16E36F]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#16E36F]">
        Application Now Open
      </span>
    </div>
    <div className="rounded-r-lg border-l-[3px] border-[#16E36F] bg-white/[0.03] px-5 py-4">
      <p className="text-sm leading-relaxed text-white sm:text-base">
        For traders who are serious about treating the markets like a business. If
        you&rsquo;re looking for a signal service or a get-rich-quick shortcut, this
        isn&rsquo;t for you.
      </p>
    </div>
  </div>
);

/* ---------------- Compact 4-item offer row ---------------- */
const COMPACT_OFFERS = [
  "12-Week 1:1 Coaching Program",
  "Personal Accountability Partner",
  "$90K Instant Funding Access",
  "Private Community + In-Person Events",
];
export const CompactOfferRow = () => (
  <div className="mx-auto grid max-w-4xl grid-cols-2 gap-3 px-4 sm:gap-4 lg:grid-cols-4" data-testid="compact-offer-row">
    {COMPACT_OFFERS.map((o) => (
      <div key={o} className={`${CARD} flex items-start gap-3 !p-4`}>
        <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0 text-[#16E36F]" />
        <span className="text-sm font-semibold leading-snug text-white">{o}</span>
      </div>
    ))}
  </div>
);

/* ---------------- Testimonial card ---------------- */
export const TestimonialCard = ({ quote, name, role }) => (
  <div className={`${CARD} flex h-full flex-col`}>
    <div className="mb-3 flex gap-1">
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={16} className="fill-[#16E36F] text-[#16E36F]" />
      ))}
    </div>
    <p className="flex-1 text-sm leading-relaxed text-slate-300">&ldquo;{quote}&rdquo;</p>
    <div className="mt-4 text-sm font-bold text-white">
      {name} <span className="font-medium text-[#16E36F]">&mdash; {role}</span>
    </div>
  </div>
);

export const MINI_TESTIMONIALS = [
  { quote: "I validated my funded account 1 week after joining. The insight you get is magic.", name: "Yohan", role: "Funded Trader" },
  { quote: "Best decision for my trading has been being a part of this community!", name: "Matthew", role: "Full-Time Trader" },
  { quote: "The insight you get from the Time and Price cycles is next level.", name: "Alik", role: "Funded Trader" },
];

export const WALL_TESTIMONIALS = [
  { quote: "Frank is amazing and such knowledgeable content.", name: "Mark", role: "Trader" },
  { quote: "I validated my funded account 1 week after joining. The insight you get is magic.", name: "Yohan", role: "Funded Trader" },
  { quote: "This made things sooo much better, I can focus more on the charts. Nuff respect.", name: "Chawayne", role: "Trader" },
  { quote: "Best decision for my trading has been being a part of this community!", name: "Matthew", role: "Full-Time Trader" },
  { quote: "Great framework to connect all the important elements into a successful strategy.", name: "Muhammad", role: "Trader" },
  { quote: "He explains things in a very structured and logical way. No unnecessary content.", name: "Leonard", role: "Trader" },
];

export const Testimonials = ({ title = "What Students Are Saying", items, cols = 3 }) => (
  <section className="mx-auto max-w-6xl px-4" data-testid="testimonials-section">
    <h2 className="mb-8 text-center text-2xl font-bold tracking-tight text-white sm:text-3xl">
      {title}
    </h2>
    <div className={`grid gap-4 sm:gap-5 ${cols === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
      {items.map((t, i) => (
        <TestimonialCard key={i} {...t} />
      ))}
    </div>
  </section>
);

/* ---------------- What happens next ---------------- */
const NEXT_STEPS = [
  { icon: FileText, title: "Submit Application", desc: "Answer 5 quick questions" },
  { icon: Phone, title: "Quick Strategy Call", desc: "Pick your own time on the spot" },
  { icon: GraduationCap, title: "Start Your 12-Week Program", desc: "Begin your transformation" },
];
export const WhatHappensNext = ({ title = "What Happens Next" }) => (
  <section className="mx-auto max-w-5xl px-4" data-testid="what-happens-next">
    <h2 className="mb-10 text-center text-2xl font-bold tracking-tight text-white sm:text-3xl">
      {title}
    </h2>
    <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
      {NEXT_STEPS.map((s, i) => (
        <React.Fragment key={s.title}>
          <div className={`${CARD} flex-1 text-center`}>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-[#16E36F]/40 bg-[#16E36F]/10">
              <s.icon size={24} className="text-[#16E36F]" />
            </div>
            <div className="text-xs font-mono font-bold uppercase tracking-widest text-[#16E36F]">
              Step {i + 1}
            </div>
            <div className="mt-1 text-base font-bold text-white">{s.title}</div>
            <div className="mt-1 text-sm text-slate-400">{s.desc}</div>
          </div>
          {i < NEXT_STEPS.length - 1 && (
            <ArrowRight size={22} className="mx-auto hidden flex-shrink-0 text-[#16E36F] sm:block" />
          )}
        </React.Fragment>
      ))}
    </div>
  </section>
);

/* ---------------- Sticky apply bar ---------------- */
export const StickyApplyBar = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[70] border-t border-[#16E36F]/30 bg-black/90 px-4 py-3 backdrop-blur-md"
      data-testid="sticky-apply-bar"
    >
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
        <span className="hidden text-sm font-semibold text-white sm:block">
          Ready to trade like a business? Spots are limited.
        </span>
        <button type="button" onClick={scrollToForm} className={`${CTA} w-full sm:w-auto`} data-testid="sticky-apply-btn">
          Apply Now <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

/* ---------------- Full 10-item offer stack ---------------- */
const FULL_OFFERS = [
  "12 Weeks of 1:1 Coaching",
  "Personal Accountability Partner",
  "12-Week Educational Program",
  "Mechanical TMU Protocol",
  "Reversal Sequence Training",
  "Algorithmic Trading Tools",
  "TMU Trading Journal",
  "Private Community Access",
  "Private In-Person Events",
  "$90K Instant Funding Access",
];
export const OfferStackGrid = () => (
  <section className="mx-auto max-w-4xl px-4" data-testid="offer-stack-grid">
    <h2 className="mb-8 text-center text-2xl font-bold tracking-tight text-white sm:text-3xl">
      What You Get Inside <span className="text-[#16E36F]">The Matrix Unlocked</span>
    </h2>
    <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
      {FULL_OFFERS.map((o) => (
        <div key={o} className="flex items-center gap-3 rounded-lg border border-[#16E36F]/15 bg-[#0D0E10] px-4 py-3.5">
          <CheckCircle2 size={20} className="flex-shrink-0 text-[#16E36F]" />
          <span className="text-sm font-semibold text-white sm:text-base">{o}</span>
        </div>
      ))}
    </div>
  </section>
);

/* ---------------- Founder credibility block ---------------- */
export const FounderBlock = () => (
  <section className="mx-auto max-w-4xl px-4" data-testid="founder-block">
    <div className={`${CARD} flex flex-col items-center gap-6 sm:flex-row sm:items-start`}>
      <img
        src={ASSETS.frankPortrait}
        alt="Frank — Founder"
        className="h-24 w-24 flex-shrink-0 rounded-full border-2 border-[#16E36F]/40 object-cover"
      />
      <div className="text-center sm:text-left">
        <h3 className="text-xl font-bold text-white">Meet Frank</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-300">
          Frank founded The Matrix Unlocked to teach traders how markets truly move
          through time and price. His mechanical framework has helped hundreds of
          traders trade with conviction instead of hope.
        </p>
        <p className="mt-3 text-sm font-bold text-[#16E36F]">
          Founder of The Matrix Unlocked · 400+ students coached · 4.92/5 across 1300+ reviews
        </p>
      </div>
    </div>
  </section>
);

/* ---------------- FAQ accordion ---------------- */
const FAQS = [
  { q: "How much time do I need per day?", a: "Our framework is built for people with lives. Most students dedicate 30-60 minutes a day. You trade to live, not live to trade." },
  { q: "Do I need trading experience?", a: "No. We coach complete beginners through to full-time traders. The 12-week program meets you where you are." },
  { q: "What markets does this work on?", a: "The Time and Price framework applies across forex, futures, indices, crypto and stocks — anywhere price moves in cycles." },
  { q: "What if I can't attend the in-person events?", a: "Events are a bonus, not a requirement. Everything core is delivered 1:1 and inside the private community online." },
  { q: "Is the $90K funding guaranteed?", a: "You get instant access to a funding path. Passing the evaluation with our protocol is what unlocks the capital." },
  { q: "What happens after I apply?", a: "You'll pick your own strategy call time right after you apply — no waiting on anyone to reach out. On the call we'll see if you're a fit for this cohort." },
];
export const FaqAccordion = () => {
  const [open, setOpen] = useState(null);
  return (
    <section className="mx-auto max-w-3xl px-4" data-testid="faq-accordion">
      <h2 className="mb-8 text-center text-2xl font-bold tracking-tight text-white sm:text-3xl">
        Frequently Asked <span className="text-[#16E36F]">Questions</span>
      </h2>
      <div className="space-y-3">
        {FAQS.map((f, i) => (
          <div key={i} className="overflow-hidden rounded-lg border border-[#16E36F]/20 bg-[#0D0E10]">
            <button
              type="button"
              onClick={() => setOpen(open === i ? null : i)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
              data-testid={`faq-toggle-${i}`}
            >
              <span className="text-sm font-semibold text-white sm:text-base">{f.q}</span>
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-[#16E36F]/40 text-[#16E36F]">
                {open === i ? <X size={14} strokeWidth={3} /> : <ChevronDown size={16} />}
              </span>
            </button>
            {open === i && (
              <div className="border-t border-[#16E36F]/10 px-5 py-4 text-sm leading-relaxed text-slate-300">
                {f.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------------- VSL video facade (click-to-play, real Vidalytics VSL) ---------------- */
// Same VSL as the current /video page.
const VIDALYTICS_ACCOUNT = "05PByEt0";
const VSL_VIDEO_ID = "jp_bjnJrqdE3jxrC";
export const VslFacade = () => {
  const [playing, setPlaying] = useState(false);
  const embedId = `vidalytics_embed_${VSL_VIDEO_ID}`;
  const onPlay = () => {
    setPlaying(true);
    track("vsl_play", { funnel: "v2" });
    whopTrack("vsl_play", { funnel: "v2" });
  };
  useEffect(() => {
    if (!playing || window.__vslV2Init) return;
    window.__vslV2Init = true;
    const base = `https://fast.vidalytics.com/embeds/${VIDALYTICS_ACCOUNT}/${VSL_VIDEO_ID}/`;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.text = `(function (v, i, d, a, l, y, t, c, s) {
      y='_'+d.toLowerCase();c=d+'L';if(!v[d]){v[d]={};}if(!v[c]){v[c]={};}if(!v[y]){v[y]={};}var vl='Loader',vli=v[y][vl],vsl=v[c][vl + 'Script'],vlf=v[c][vl + 'Loaded'],ve='Embed';
      if (!vsl){vsl=function(u,cb){
        if(t){cb();return;}s=i.createElement("script");s.type="text/javascript";s.async=1;s.src=u;
        if(s.readyState){s.onreadystatechange=function(){if(s.readyState==="loaded"||s.readyState=="complete"){s.onreadystatechange=null;vlf=1;cb();}};}else{s.onload=function(){vlf=1;cb();};}
        i.getElementsByTagName("head")[0].appendChild(s);
      };}
      vsl(l+'loader.min.js',function(){if(!vli){var vlc=v[c][vl];vli=new vlc();}vli.loadScript(l+'player.min.js',function(){var vec=v[d][ve];t=new vec();t.run(a);});});
    })(window, document, 'Vidalytics', '${embedId}', '${base}');`;
    document.body.appendChild(script);
  }, [playing, embedId]);
  return (
    <div
      className="relative mx-auto aspect-video w-full max-w-3xl overflow-hidden rounded-2xl border-2 border-[#16E36F]/30 bg-black shadow-[0_20px_80px_rgba(0,0,0,0.6)]"
      data-testid="vsl-facade"
    >
      {!playing ? (
        <button
          type="button"
          onClick={onPlay}
          className="group absolute inset-0 h-full w-full"
          data-testid="vsl-play-btn"
        >
          <img
            src="https://images.unsplash.com/photo-1628763228263-d9ebb07bd0d9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1280"
            alt="Watch the training"
            className="h-full w-full object-cover opacity-70 transition-opacity group-hover:opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />
          <div className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#16E36F] shadow-[0_0_40px_rgba(22,227,111,0.6)] transition-transform group-hover:scale-110">
            <Play size={32} className="ml-1 fill-black text-black" />
          </div>
          <div className="absolute inset-x-0 bottom-5 text-center text-lg font-extrabold uppercase tracking-wide text-white drop-shadow">
            Watch This Before Your Next Trade
          </div>
        </button>
      ) : (
        <div id={embedId} className="absolute inset-0 h-full w-full" data-testid="vsl-embed" />
      )}
    </div>
  );
};

/* ---------------- Calendly inline (dark theme, brand green) ---------------- */
const CALENDLY_URL =
  "https://calendly.com/d/dtd2-p3j-658/strategy-call-tmu?hide_event_type_details=1&hide_gdpr_banner=1&background_color=0a0f0b&text_color=ffffff&primary_color=16e36f";

const CalendlyInline = ({ onScheduled, prefill }) => {
  const containerRef = useRef(null);
  const [height, setHeight] = useState(720);
  useEffect(() => {
    const init = () => {
      if (window.Calendly && containerRef.current) {
        containerRef.current.innerHTML = "";
        let url = CALENDLY_URL;
        if (prefill && (prefill.name || prefill.email)) {
          const parts = [];
          if (prefill.name) parts.push(`name=${encodeURIComponent(prefill.name)}`);
          if (prefill.email) parts.push(`email=${encodeURIComponent(prefill.email)}`);
          url = `${CALENDLY_URL}&${parts.join("&")}`;
        }
        const opts = { url, parentElement: containerRef.current };
        if (prefill && (prefill.name || prefill.email)) {
          opts.prefill = { name: prefill.name || "", email: prefill.email || "" };
        }
        const utm = getCalendlyUtm();
        if (utm) opts.utm = utm;
        window.Calendly.initInlineWidget(opts);
      }
    };
    const id = "calendly-widget-js";
    let script = document.getElementById(id);
    if (window.Calendly) {
      init();
    } else if (script) {
      script.addEventListener("load", init);
    } else {
      script = document.createElement("script");
      script.id = id;
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      script.addEventListener("load", init);
      document.body.appendChild(script);
    }
    return () => script && script.removeEventListener("load", init);
  }, [prefill?.name, prefill?.email]);
  useEffect(() => {
    const handler = (e) => {
      if (!e.data || typeof e.data !== "object") return;
      if (e.data.event === "calendly.event_scheduled") {
        onScheduled && onScheduled(e.data.payload || {});
      }
      // Auto-resize the iframe so Calendly's form never gets cramped on mobile.
      if (e.data.event === "calendly.page_height" && e.data.payload && e.data.payload.height) {
        const h = parseInt(String(e.data.payload.height), 10);
        if (h && h > 400) setHeight(h);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onScheduled]);
  return (
    <div
      ref={containerRef}
      style={{ minWidth: "100%", width: "100%", height: `${height}px` }}
      data-testid="v2-calendly-embed"
    />
  );
};

const fmtWhen = (iso, tz) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "long", month: "long", day: "numeric",
      hour: "numeric", minute: "2-digit",
      timeZone: tz || undefined, timeZoneName: "short",
    });
  } catch {
    return new Date(iso).toLocaleString();
  }
};

const gcalLink = ({ start_time, end_time, join_url }) => {
  if (!start_time) return null;
  const z = (s) => s && new Date(s).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const end = end_time || new Date(new Date(start_time).getTime() + 30 * 60000).toISOString();
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: "Strategy Call — The Matrix Unlocked",
    dates: `${z(start_time)}/${z(end)}`,
    details: `Your 30-minute strategy call with The Matrix Unlocked.${join_url ? `\n\nJoin: ${join_url}` : ""}`,
  });
  if (join_url) params.set("location", join_url);
  return `https://www.google.com/calendar/render?${params.toString()}`;
};

/* ---------------- 5-step application form ---------------- */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STEPS = [
  { id: "email", type: "email", title: "Email Address", helper: "We'll send your application details here", placeholder: "name@example.com" },
  { id: "full_name", type: "text", title: "Full Name", helper: "Include your first & last name", placeholder: "Type your answer here..." },
  { id: "trading_experience", type: "choice", title: "Trading Experience Level", choices: ["Beginner", "Intermediate", "Advanced", "Full-time"] },
  { id: "capital", type: "choice", title: "Available Capital to Trade With", choices: ["Under $5K", "$5K-$25K", "$25K-$100K", "$100K+"] },
  { id: "challenge", type: "textarea", title: "What's your biggest challenge right now?", helper: "One or two sentences is perfect", placeholder: "Type your answer here..." },
];
// Map v2 fields onto the existing Lead schema (no backend change needed).
const LEAD_FIELD = {
  email: "email",
  full_name: "full_name",
  trading_experience: "trading_experience",
  capital: "portfolio_size",
  challenge: "trading_goals",
};

export const ApplyFormV2 = ({ funnel = "apply_v2" }) => {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");
  const [stage, setStage] = useState("form"); // form | calendar | booked
  const [booking, setBooking] = useState(null);
  const [walletOpen, setWalletOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const leadIdRef = useRef(sessionStorage.getItem("tmu_v2_lead_id") || null);
  const bookedRef = useRef(false);
  const step = STEPS[idx];
  const total = STEPS.length;

  useEffect(() => {
    track("v2_form_step_view", { funnel, step: idx + 1, id: step.id });
  }, [idx]); // eslint-disable-line

  const ensureLead = async () => {
    if (leadIdRef.current) return leadIdRef.current;
    try {
      const res = await axios.post(`${API}/leads`, {
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        utm: getStoredUTM(),
      });
      leadIdRef.current = res.data.id;
      sessionStorage.setItem("tmu_v2_lead_id", res.data.id);
      return res.data.id;
    } catch {
      return null;
    }
  };

  const patchLead = async (fields) => {
    const id = await ensureLead();
    if (!id) return;
    try {
      await axios.patch(`${API}/leads/${id}`, fields);
    } catch {
      /* never block the user */
    }
  };

  const validate = (val) => {
    if (step.type === "email") return EMAIL_RE.test((val || "").trim());
    return (val || "").toString().trim().length > 0;
  };

  const advance = async (val) => {
    const mapped = LEAD_FIELD[step.id];
    const payload = { [mapped]: val, last_question: idx + 1 };
    if (idx === total - 1) {
      setSubmitting(true);
      await patchLead({ ...payload, status: "completed" });
      const email = answers.email;
      const parts = String(answers.full_name || "").trim().split(/\s+/);
      track("submit_application", { email, funnel });
      fireRegistrationConversion({
        email,
        firstName: parts[0] || "",
        lastName: parts.slice(1).join(" ") || "",
        transactionId: email ? `appv2_${email}` : undefined,
      });
      fireGoogleAdsConversion(PIXEL_CONFIG.GADS_APPLICATION_SUBMIT, {
        eventKey: "application_submit",
        email,
        transactionId: email ? `appv2_${email}` : undefined,
      });
      sessionStorage.removeItem("tmu_v2_lead_id");
      setStage("calendar");
      setSubmitting(false);
      setTimeout(() => scrollToForm(), 100);
      return;
    }
    patchLead(payload);
    setIdx((i) => i + 1);
    setError("");
  };

  const handleScheduled = async (payload) => {
    if (bookedRef.current) return;
    bookedRef.current = true;
    track("call_booked", { funnel, source: "inline_calendar" });
    whopTrack("call_booked", {
      funnel,
      event_id: payload?.event?.uri ? `call_${payload.event.uri}` : undefined,
    });
    let data = {};
    try {
      const res = await axios.post(`${API}/vsl/booking`, {
        invitee_uri: payload?.invitee?.uri,
        event_uri: payload?.event?.uri,
        funnel,
        visitor_id: getVisitorId(),
        utm: getStoredUTM(),
      });
      data = res.data || {};
    } catch {
      /* still show booked confirmation */
    }
    setBooking(data);
    setStage("booked");
    if (data && data.id) setWalletOpen(true);
    setTimeout(() => scrollToForm(), 100);
  };

  const onContinue = () => {
    const val = answers[step.id];
    if (!validate(val)) {
      setError(step.type === "email" ? "Please enter a valid email" : "Please fill this in");
      return;
    }
    advance(val);
  };

  const onChoice = (choice) => {
    setAnswers((p) => ({ ...p, [step.id]: choice }));
    setError("");
    setTimeout(() => advance(choice), 250);
  };

  const setVal = (v) => {
    setAnswers((p) => ({ ...p, [step.id]: v }));
    setError("");
  };

  const progress = stage !== "form" ? 100 : (idx / total) * 100;

  return (
    <div
      id="apply-form"
      className={`mx-auto w-full ${stage === "calendar" ? "max-w-3xl" : "max-w-xl"} scroll-mt-6 overflow-hidden rounded-2xl border border-[#16E36F]/25 bg-[#0a0f0b] shadow-[0_20px_80px_rgba(0,0,0,0.6)] transition-all duration-300`}
      data-testid="application-form-v2"
    >
      <div className="h-1.5 w-full bg-white/10">
        <div className="h-full bg-[#16E36F] transition-all duration-300" style={{ width: `${Math.max(progress, 3)}%` }} />
      </div>
      <div className="p-6 sm:p-8">
        {stage === "calendar" ? (
          <div data-testid="v2-calendar-stage">
            <h3 className="text-center text-2xl font-extrabold sm:text-3xl">
              <span className="block text-white">You're Almost In.</span>
              <span className="block text-[#16E36F]">Pick Your Strategy Call Time Below.</span>
            </h3>
            <p className="mx-auto mt-3 max-w-md text-center text-sm text-slate-300 sm:text-base">
              Choose a time that works for you. Frank or a member of his team will personally walk
              you through the program on this call.
            </p>
            <div className="mt-6 overflow-hidden rounded-xl border-2 border-[#16E36F]/40 bg-[#0a0f0b]">
              <CalendlyInline
                onScheduled={handleScheduled}
                prefill={{ name: answers.full_name, email: answers.email }}
              />
            </div>
            <p className="mt-4 text-center text-xs text-slate-400">
              Your application has been saved. This call is a 30-minute strategy conversation to
              see if The Matrix Unlocked is the right fit for your trading goals.
            </p>
          </div>
        ) : stage === "booked" ? (
          <div className="py-8 text-center" data-testid="v2-booked-stage">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#16E36F]/15">
              <CalendarCheck size={34} strokeWidth={2.5} className="text-[#16E36F]" />
            </div>
            <h3 className="text-2xl font-extrabold text-white sm:text-3xl">You're Booked.</h3>
            <p className="mt-3 text-slate-300">Check your email for confirmation and call details.</p>
            {booking?.start_time && (
              <div className="mx-auto mt-5 flex items-center justify-center gap-2 rounded-lg border border-[#16E36F]/30 bg-[#16E36F]/10 px-4 py-3">
                <CalendarCheck size={18} className="text-[#16E36F]" />
                <span className="text-sm font-bold text-[#16E36F]" data-testid="v2-booked-time">
                  {fmtWhen(booking.start_time, booking.timezone)}
                </span>
              </div>
            )}
            {gcalLink(booking || {}) && (
              <a
                href={gcalLink(booking)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#16E36F] underline-offset-4 hover:underline"
                data-testid="v2-add-to-calendar"
              >
                <CalendarCheck size={16} /> Add to Calendar
              </a>
            )}
            {booking?.id && (
              <div className="mt-6 flex justify-center">
                <a
                  href={`${API}/wallet/pass/${booking.id}`}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-black px-5 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.03]"
                  data-testid="v2-add-to-wallet-inline"
                >
                  <Apple size={20} className="fill-white text-white" />
                  Add to Apple Wallet
                </a>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#16E36F]">
                {idx + 1} / {total}
              </span>
            </div>
            <h3 className="mt-2 text-xl font-bold text-white sm:text-2xl">{step.title}</h3>
            {step.helper && <p className="mt-1 text-sm text-slate-400">{step.helper}</p>}

            <div className="mt-5">
              {step.type === "choice" ? (
                <div className="grid gap-3">
                  {step.choices.map((c, i) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => onChoice(c)}
                      className={`flex items-center justify-between rounded-lg border px-4 py-3.5 text-left text-sm font-semibold transition-all ${
                        answers[step.id] === c
                          ? "border-[#16E36F] bg-[#16E36F]/10 text-white"
                          : "border-white/15 bg-white/[0.02] text-slate-200 hover:border-[#16E36F]/50"
                      }`}
                      data-testid={`v2-choice-${step.id}-${i}`}
                    >
                      {c}
                      {answers[step.id] === c && <Check size={18} strokeWidth={3} className="text-[#16E36F]" />}
                    </button>
                  ))}
                </div>
              ) : step.type === "textarea" ? (
                <textarea
                  rows={3}
                  value={answers[step.id] || ""}
                  onChange={(e) => setVal(e.target.value)}
                  placeholder={step.placeholder}
                  autoFocus
                  className="w-full resize-none rounded-lg border border-white/15 bg-white/[0.03] px-4 py-3 text-white outline-none transition-colors focus:border-[#16E36F]"
                  data-testid={`v2-input-${step.id}`}
                />
              ) : (
                <input
                  type={step.type === "email" ? "email" : "text"}
                  value={answers[step.id] || ""}
                  onChange={(e) => setVal(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onContinue()}
                  placeholder={step.placeholder}
                  autoFocus
                  className="w-full rounded-lg border border-white/15 bg-white/[0.03] px-4 py-3 text-white outline-none transition-colors focus:border-[#16E36F]"
                  data-testid={`v2-input-${step.id}`}
                />
              )}
              {error && <p className="mt-2 text-sm text-red-400" data-testid="v2-form-error">{error}</p>}

              {step.type !== "choice" && (
                <button
                  type="button"
                  onClick={onContinue}
                  disabled={submitting}
                  className={`${CTA} mt-5 w-full disabled:opacity-60`}
                  data-testid="v2-continue-btn"
                >
                  {idx === total - 1 ? (submitting ? "Submitting…" : "Apply Now") : "Continue"}
                  <ArrowRight size={18} />
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {walletOpen && booking?.id && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          data-testid="wallet-popup"
        >
          <div className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-[#16E36F]/30 bg-[#0a0f0b] p-7 text-center shadow-[0_30px_120px_rgba(0,0,0,0.8)]">
            <button
              type="button"
              onClick={() => setWalletOpen(false)}
              aria-label="Close"
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
              data-testid="wallet-popup-close"
            >
              <XIcon size={18} />
            </button>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#16E36F]/15">
              <Apple size={28} className="fill-[#16E36F] text-[#16E36F]" />
            </div>
            <h4 className="text-xl font-extrabold text-white">Save your call to Apple Wallet</h4>
            <p className="mt-2 text-sm text-slate-300">
              Add your strategy call as a pass so the time and join link are always on your phone.
            </p>
            {booking?.start_time && (
              <p className="mt-3 text-sm font-bold text-[#16E36F]">
                {fmtWhen(booking.start_time, booking.timezone)}
              </p>
            )}
            <a
              href={`${API}/wallet/pass/${booking.id}`}
              onClick={() => track("add_to_wallet_click", { funnel })}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-black px-5 py-3.5 text-base font-semibold text-white ring-1 ring-white/15 transition-transform hover:scale-[1.02]"
              data-testid="wallet-popup-add-btn"
            >
              <Apple size={22} className="fill-white text-white" />
              Add to Apple Wallet
            </a>
            <p className="mt-3 text-[11px] leading-snug text-slate-500" data-testid="wallet-desktop-note">
              If you are booking from your desktop, check your email from your iPhone and claim your pass.
            </p>
            <button
              type="button"
              onClick={() => setWalletOpen(false)}
              className="mt-3 text-xs font-medium text-slate-400 hover:text-slate-200"
              data-testid="wallet-popup-later"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- CTA button (scrolls to form) ---------------- */
export const ApplyCTA = ({ label = "Apply Now", testid = "apply-cta" }) => (
  <div className="flex justify-center px-4">
    <button type="button" onClick={scrollToForm} className={CTA} data-testid={testid}>
      {label} <ArrowRight size={18} />
    </button>
  </div>
);
